// Borrower support inbox — lives inside ChatWidget as a second tab.
//
// Self-contained on purpose: it shares the widget's shell and visual language
// but none of its data path, because borrower threads are in `bapp_messages`
// (see borrowerSupport.service.ts for why they can't use the team-chat tables).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Box, CircularProgress, IconButton, InputBase, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { supabase } from '../../lib/supabase';
import { CT, avatarTone, initialsOf, shortTime } from './chatTheme';
import {
  listBorrowerThreads, loadBorrowerThread, replyToBorrower, markBorrowerThreadRead,
  markAllBorrowerRead, BorrowerThread, BorrowerMessage,
} from './borrowerSupport.service';

interface Props {
  /** Unread borrower messages per conversation. Owned by ChatWidget so the
   *  launcher badge is right even when this panel has never been mounted. */
  unread: Record<string, number>;
  onUnreadChange: () => void;
}

const nameOf = (t: BorrowerThread) =>
  (t.full_name || '').trim() || t.unique_number || 'Borrower';

const BorrowerSupportPanel: React.FC<Props> = ({ unread, onUnreadChange }) => {
  const [threads, setThreads] = useState<BorrowerThread[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [active, setActive] = useState<BorrowerThread | null>(null);
  const [messages, setMessages] = useState<BorrowerMessage[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  const refreshList = useCallback(async () => {
    setLoadingList(true);
    try {
      setThreads(await listBorrowerThreads());
      setError(null);
    } catch (e: any) {
      setError(e?.message ?? 'Could not load borrower messages');
    } finally {
      setLoadingList(false);
    }
  }, []);

  const openThread = useCallback(async (t: BorrowerThread) => {
    setActive(t);
    setLoadingThread(true);
    try {
      setMessages(await loadBorrowerThread(t.conversation_id));
      // Reading the thread is what clears its badge.
      await markBorrowerThreadRead(t.conversation_id);
      onUnreadChange();
    } catch (e: any) {
      setError(e?.message ?? 'Could not open the conversation');
    } finally {
      setLoadingThread(false);
    }
  }, [onUnreadChange]);

  useEffect(() => { void refreshList(); }, [refreshList]);

  // Live: a borrower sending while staff has the panel open should appear at
  // once, in the list and in the open thread.
  useEffect(() => {
    const ch = supabase
      .channel('bapp-support-staff')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'bapp_messages' }, (payload) => {
        const row = payload.new as BorrowerMessage;
        setThreads((prev) => {
          const next = prev.map((t) => (t.conversation_id === row.conversation_id
            ? { ...t, last_body: row.body, last_message_at: row.created_at,
                borrower_msgs: t.borrower_msgs + (row.sender === 'borrower' ? 1 : 0) }
            : t));
          // A brand-new borrower thread won't be in the list yet.
          if (!next.some((t) => t.conversation_id === row.conversation_id)) void refreshList();
          return next.sort((a, b) =>
            String(b.last_message_at ?? '').localeCompare(String(a.last_message_at ?? '')));
        });
        setMessages((prev) => (active && row.conversation_id === active.conversation_id && !prev.some((m) => m.id === row.id)
          ? [...prev, row] : prev));
        // Arriving in the thread staff already has open counts as read.
        if (active && row.conversation_id === active.conversation_id && row.sender === 'borrower') {
          void markBorrowerThreadRead(row.conversation_id).then(onUnreadChange).catch(() => {});
        }
      })
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [active, refreshList, onUnreadChange]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages]);

  const send = async () => {
    const body = draft.trim();
    if (!body || !active || sending) return;
    setDraft('');
    setSending(true);
    try {
      await replyToBorrower(active, body);
      setMessages(await loadBorrowerThread(active.conversation_id));
    } catch (e: any) {
      setDraft(body);            // never silently lose what they typed
      setError(e?.message ?? 'Could not send. Has the staff-reply policy been applied?');
    } finally {
      setSending(false);
    }
  };

  const waiting = useMemo(
    () => threads.filter((t) => (unread[t.conversation_id] ?? 0) > 0).length,
    [threads, unread],
  );
  const totalUnread = useMemo(
    () => Object.values(unread).reduce((sum, n) => sum + n, 0),
    [unread],
  );

  const markAllRead = useCallback(async () => {
    try {
      await markAllBorrowerRead();
      onUnreadChange();
    } catch (e: any) {
      setError(e?.message ?? 'Could not mark the inbox as read');
    }
  }, [onUnreadChange]);

  // ---------- thread ----------
  if (active) {
    const tone = avatarTone(active.user_id);
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, bgcolor: CT.bg }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 1, borderBottom: `1px solid ${CT.border}`, bgcolor: CT.white }}>
          <IconButton size="small" onClick={() => setActive(null)} aria-label="Back to borrower list">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Avatar sx={{ width: 30, height: 30, bgcolor: tone.bg, color: tone.fg, fontSize: 12, fontWeight: 700 }}>
            {initialsOf(nameOf(active))}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography noWrap sx={{ fontWeight: 700, fontSize: 13.5, color: CT.ink }}>{nameOf(active)}</Typography>
            <Typography noWrap sx={{ fontSize: 10.5, color: CT.muted, fontFamily: CT.mono }}>
              {active.unique_number ?? '—'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {loadingThread ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={20} /></Box>
          ) : messages.map((m) => {
            const mine = m.sender !== 'borrower';   // staff/bot on the right
            return (
              <Box key={m.id} sx={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '82%' }}>
                <Box sx={{
                  bgcolor: mine ? CT.green : CT.white,
                  color: mine ? '#fff' : CT.ink,
                  border: mine ? 'none' : `1px solid ${CT.border}`,
                  borderRadius: `${CT.radius.lg}px`,
                  borderBottomRightRadius: mine ? 4 : CT.radius.lg,
                  borderBottomLeftRadius: mine ? CT.radius.lg : 4,
                  px: 1.5, py: 1,
                }}>
                  <Typography sx={{ fontSize: 13.5, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {m.body}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 9.5, color: CT.faint, mt: 0.25, textAlign: mine ? 'right' : 'left', fontFamily: CT.mono }}>
                  {m.sender === 'borrower' ? '' : 'You · '}{shortTime(m.created_at)}
                </Typography>
              </Box>
            );
          })}
          <div ref={endRef} />
        </Box>

        {error && (
          <Typography sx={{ fontSize: 11, color: '#B42318', px: 1.5, pb: 0.5 }}>{error}</Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, p: 1.25, borderTop: `1px solid ${CT.border}`, bgcolor: CT.white }}>
          <InputBase
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
            placeholder={`Reply to ${nameOf(active)}…`}
            multiline
            maxRows={4}
            sx={{ flex: 1, bgcolor: CT.bone, borderRadius: `${CT.radius.md}px`, px: 1.5, py: 0.75, fontSize: 13.5 }}
          />
          <IconButton
            onClick={() => void send()}
            disabled={!draft.trim() || sending}
            sx={{ bgcolor: CT.green, color: '#fff', '&:hover': { bgcolor: CT.greenHover }, '&.Mui-disabled': { bgcolor: CT.border, color: '#fff' } }}
            aria-label="Send reply"
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  }

  // ---------- list ----------
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, bgcolor: CT.bg }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderBottom: `1px solid ${CT.border}`, bgcolor: CT.white }}>
        <Typography sx={{ flex: 1, fontSize: 11, color: CT.muted, fontFamily: CT.mono, letterSpacing: 0.6, textTransform: 'uppercase' }}>
          {threads.length} conversation{threads.length === 1 ? '' : 's'}
          {waiting > 0 ? ` · ${waiting} waiting` : ''}
        </Typography>
        <Tooltip title="Mark all as read">
          <span>
            <IconButton size="small" onClick={() => void markAllRead()} disabled={totalUnread === 0}
              aria-label="Mark all borrower messages as read">
              <DoneAllIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => void refreshList()} aria-label="Refresh borrower list">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {loadingList ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
        ) : error ? (
          <Typography sx={{ p: 2, fontSize: 12.5, color: '#B42318' }}>{error}</Typography>
        ) : threads.length === 0 ? (
          <Typography sx={{ p: 3, fontSize: 12.5, color: CT.muted, textAlign: 'center' }}>
            No borrower messages yet.
          </Typography>
        ) : threads.map((t) => {
          const tone = avatarTone(t.user_id);
          const count = unread[t.conversation_id] ?? 0;
          return (
            <Box
              key={t.conversation_id}
              role="button"
              tabIndex={0}
              onClick={() => void openThread(t)}
              onKeyDown={(e) => { if (e.key === 'Enter') void openThread(t); }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.25,
                bgcolor: count > 0 ? CT.greenTint : 'transparent',
                px: 1.5, py: 1.25, cursor: 'pointer',
                borderBottom: `1px solid ${CT.hairline}`,
                transition: CT.motion,
                '&:hover': { bgcolor: count > 0 ? CT.greenTint : CT.bone },
              }}
            >
              <Avatar sx={{ width: 34, height: 34, bgcolor: tone.bg, color: tone.fg, fontSize: 12.5, fontWeight: 700 }}>
                {initialsOf(nameOf(t))}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap sx={{ fontWeight: count ? 800 : 700, fontSize: 13.5, color: CT.ink }}>{nameOf(t)}</Typography>
                <Typography noWrap sx={{ fontSize: 12, color: count ? CT.inkSoft : CT.muted, fontWeight: count ? 600 : 400 }}>
                  {t.last_body ?? ''}
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
                <Typography sx={{ fontSize: 10, color: CT.faint, fontFamily: CT.mono }}>
                  {t.last_message_at ? shortTime(t.last_message_at) : ''}
                </Typography>
                {count > 0 && (
                  <Box sx={{
                    mt: 0.5, ml: 'auto', minWidth: 18, height: 18, px: 0.5,
                    display: 'grid', placeItems: 'center', borderRadius: 999,
                    bgcolor: CT.pink, color: '#fff', fontSize: 10.5, fontWeight: 700,
                  }}>
                    {count > 99 ? '99+' : count}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default BorrowerSupportPanel;
